<template>
  <b-modal
    id="save-layout-modal"
    :title="
      layout_id ? $t('layout_builder.action_update') : $t('layout_builder.action_add')
    "
    @ok="handleSubmit"
    @hidden="resetModal"
    ok-title="Guardar"
    centered
    no-close-on-backdrop
  >
    <validation-observer ref="refFormObserver">
      <b-form @submit.prevent>
        <!-- Layout Name -->
        <b-form-group :label="$t('layout_builder.fields.name.label')">
          <validation-provider
            #default="validationContext"
            name="Layout name"
            rules="required"
          >
            <b-form-input
              v-model="formData.layoutName"
              :placeholder="$t('layout_builder.fields.name.label')"
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <!-- Tenant -->
        <b-form-group :label="$t('layout_builder.fields.tenant.label')">
          <v-select
            v-model="formData.selectedTenant"
            :options="tenantOptions"
            :reduce="(val) => val.value"
            @input="tenantChanged"
          />
        </b-form-group>

        <!-- Users -->
        <b-form-group :label="$t('layout_builder.fields.users.label')">
          <validation-provider
            #default="validationContext"
            name="Users"
            :rules="!isAdmin ? 'required' : null"
          >
            <v-select
              v-model="formData.selectedUsers"
              :options="usersOptions"
              :reduce="(val) => val.value"
              multiple
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <!-- Options -->
        <b-row>
          <b-col cols="4" v-if="layout_id">
            <b-form-group :label="$t('layout_builder.fields.create_new_layout.label')">
              <b-form-checkbox v-model="formData.createNewLayout" switch />
            </b-form-group>
          </b-col>
          <!-- Is default -->
          <!-- <b-col v-if="isAdmin" cols="4">
            <b-form-group :label="$t('layout_builder.fields.default.label')">
              <b-form-checkbox v-model="formData.isDefault" switch />
            </b-form-group>
          </b-col> -->

          <!-- Is card -->
          <b-col cols="4">
            <b-form-group :label="$t('layout_builder.fields.dashboard_mode.label')">
              <b-form-checkbox v-model="formData.isCard" switch />
            </b-form-group>
          </b-col>

          <!-- Card distance -->
          <b-col cols="4" v-if="formData.isCard">
            <b-form-group :label="$t('layout_builder.fields.card_distance.label')">
              <validation-provider
                #default="validationContext"
                name="Card distance"
                rules="required|between:2,25"
              >
                <b-form-input
                  v-model="formData.cardDistance"
                  type="number"
                  min="2"
                  max="25"
                  step="1"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  onMounted,
  nextTick,
  getCurrentInstance,
  watch,
} from "@vue/composition-api";
import store from "@/store";
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormInvalidFeedback,
  BRow,
  BCol,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";

export default {
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormInvalidFeedback,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
  },
  props: {
    layout_id: {
      type: [Number, String],
      default: null,
    },
    layoutSlots: {
      type: Array,
      required: true,
    },
    layoutName: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const { refFormObserver, getValidationState } = formValidation();

    const user = computed(() => store.getters["user/getUser"]);
    const isAdmin = computed(() => user.value.is_master_admin);

    const tenantOptions = ref([]);
    const usersOptions = ref([]);

    const instance = getCurrentInstance();

    const formData = reactive({
      layoutName: props.layoutName || `$${props.layoutSlots.length}`,
      selectedTenant: null,
      selectedUsers: [],
      isDefault: false,
      isCard: false,
      cardDistance: 0,
      createNewLayout: false,
    });

    // Helpers
    const toUniqueIds = (arr) =>
      Array.from(new Set((arr || []).filter((v) => v !== null && v !== undefined)));

    const loadLayoutData = async (layoutId) => {
      if (!layoutId) return;

      try {
        const response = await store.dispatch("layouts/getLayout", layoutId);
        const layout = response.data;

        formData.layoutName = layout.name;
        formData.selectedTenant = layout.id_tenant;
        formData.isDefault = layout.is_default === 1;
        formData.isCard = layout.is_card === 1;
        formData.cardDistance = layout.distance || 0;

        if (layout.id_tenant) {
          await tenantChanged(layout.id_tenant);

          // Get valid user list from current tenant
          const currentTenantUsersResponse = await store.dispatch(
            "tenant/getTenantUsers",
            layout.id_tenant
          );
          const validUserIds = new Set(
            currentTenantUsersResponse.data.map((user) => user.id)
          );

          const rawUsers = Array.isArray(layout.users) ? layout.users : [];
          let selected = [];

          if (rawUsers.length > 0) {
            // Extract unique user IDs and filter only those belonging to the tenant
            const userIds = rawUsers
              .map((u) => (typeof u === "object" && u !== null ? u.id : u))
              .filter((id) => id !== null && id !== undefined && validUserIds.has(id));

            selected = Array.from(new Set(userIds)); // Remove duplicates
          }

          formData.selectedUsers = selected;

          // Add missing users to options if they are not present
          const existingOptionIds = new Set(usersOptions.value.map((o) => o.value));
          const missingUsers = rawUsers.filter((u) => {
            const id = typeof u === "object" && u !== null ? u.id : u;
            return id && validUserIds.has(id) && !existingOptionIds.has(id);
          });

          if (missingUsers.length > 0) {
            const extraOptions = missingUsers.map((u) => ({
              value: typeof u === "object" ? u.id : u,
              label: typeof u === "object" ? u.name : `User ${u}`,
            }));
            usersOptions.value = [...usersOptions.value, ...extraOptions];
          }
        }

        if (
          layout.id_tenant &&
          !tenantOptions.value.some((t) => t.value === layout.id_tenant)
        ) {
          tenantOptions.value.push({
            value: layout.id_tenant,
            label: layout.tenant?.name || `Tenant ${layout.id_tenant}`,
          });
        }
      } catch (error) {
        return;
      }
    };

    onMounted(async () => {
      try {
        const response = await store.dispatch("tenant/getAll");
        tenantOptions.value = response.data.map((tenant) => ({
          value: tenant.id,
          label: tenant.name,
        }));
      } catch (error) {
        console.error("Error loading tenants:", error);
      }

      await loadLayoutData(props.layout_id);
    });

    const tenantChanged = async (tenantId) => {
      if (!tenantId) {
        usersOptions.value = [];
        formData.selectedUsers = [];
        return;
      }

      try {
        const response = await store.dispatch("tenant/getTenantUsers", tenantId);
        usersOptions.value = response.data.map((user) => ({
          value: user.id,
          label: user.name,
        }));

        // Remove selected users that do not belong to the new tenant
        const validUserIds = new Set(response.data.map((user) => user.id));
        formData.selectedUsers = formData.selectedUsers.filter((userId) =>
          validUserIds.has(userId)
        );
      } catch (error) {
        console.error("Error loading users:", error);
        usersOptions.value = [];
        formData.selectedUsers = [];
      }
    };

    const handleSubmit = async (bvModalEvent) => {
      bvModalEvent.preventDefault();

      try {
        const isValid = await refFormObserver.value.validate();
        if (!isValid) return;

        if (!props.layoutSlots || !Array.isArray(props.layoutSlots)) {
          console.error("Error: layoutSlots is not a valid array");
          return;
        }

        const enhancedSlots = props.layoutSlots.map((slot) => ({
          ...slot,
          minW: slot.minW || 1,
          minH: slot.minH || 1,
          content: slot.content || `${slot.w} x ${slot.h}`,
          name: slot.name || null,
          type: slot.type || null,
          data: slot.data || null,
        }));

        // Normalize and deduplicate users before saving
        const cleanUsers = toUniqueIds(formData.selectedUsers);

        const saveData = {
          name: formData.layoutName,
          data: enhancedSlots,
          layout_id: !formData.createNewLayout ? props.layout_id : null,
          id_tenant: formData.selectedTenant,
          users: cleanUsers,
          is_default: formData.isDefault,
          is_card: formData.isCard,
          distance: formData.cardDistance,
          updateCurrent: !formData.createNewLayout,
        };

        emit("save", saveData);

        if (instance && instance.proxy && instance.proxy.$root) {
          instance.proxy.$root.$bvModal.hide("save-layout-modal");
        }
      } catch (error) {
        console.error("Error saving layout:", error);
        // Do not close the modal in case of an error to allow corrections
      }
    };

    const resetModal = () => {
      // Always reset form data when closing the modal
      formData.layoutName = props.layoutName || ``;
      formData.selectedTenant = null;
      formData.selectedUsers = [];
      formData.isDefault = false;
      formData.isCard = false;
      formData.cardDistance = 0;
      formData.createNewLayout = false;

      // Clear user options to force reload
      usersOptions.value = [];
    };

    watch(
      () => formData.isCard,
      (newValue) => {
        if (!newValue) {
          formData.cardDistance = 0;
        } else if (formData.cardDistance === 0) {
          formData.cardDistance = 5;
        }
      }
    );

    // Watch to reload data when layout_id changes
    watch(() => props.layout_id, loadLayoutData, { immediate: false });

    return {
      formData,
      tenantOptions,
      usersOptions,
      user,
      isAdmin,
      handleSubmit,
      tenantChanged,
      resetModal,
      refFormObserver,
      getValidationState,
    };
  },
};
</script>

<style lang="scss">
.modal .modal-header {
  background-color: #1b3d5e;
}
</style>
